/**
 * useInquiryMutation — public lead-capture mutation.
 *
 * Wraps the `request-quote` Edge Function (PROJECT_STATE task #60) so
 * the landing-page form has a single typed entry point. The Edge
 * Function is unauthenticated; the form does NOT pass a JWT, and the
 * supabase JS client respects that for `functions.invoke` calls
 * coming through the public anon key.
 *
 * Error handling mirrors the established pattern (useApproveTask,
 * useForgotPin): pull the structured `{ error, detail }` shape out of
 * the function response, translate known codes into visitor-friendly
 * userMessages, and surface unknown ones verbatim. Validation /
 * rate-limit / DB / audit failures all surface their own code so the
 * landing-page form can show a precise error rather than a generic
 * "something went wrong."
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                          from './useSupabase'
import { AppError }                             from '@/lib/errors/AppError'
import type { Vertical, InquiryType }           from '@/types/landing'

export interface InquiryRequest {
  email:           string
  name?:           string
  company?:        string
  vertical:        Vertical
  inquiry_type:    InquiryType
  message?:        string
  rooms_count?:    number
  properties_count?: number
  /** Honeypot — humans never fill this; bots usually do. Pass through verbatim. */
  website?:        string
  utm_source?:     string
  utm_medium?:     string
  utm_campaign?:   string
}

export interface InquiryResult {
  ok:          true
  /** Lead ID is omitted on the silent_ok (honeypot) branch. */
  lead_id?:    string
  email_sent?: boolean
}

export function useInquiryMutation(): UseMutationResult<InquiryResult, AppError, InquiryRequest> {
  const supabase = useSupabase()

  return useMutation<InquiryResult, AppError, InquiryRequest>({
    mutationFn: async (req) => {
      const { data, error } = await supabase.functions.invoke<InquiryResult>(
        'request-quote',
        { body: req },
      )

      if (error) {
        // Supabase wraps non-2xx responses; the structured error body
        // is the Response in `context`; read it via .clone().json().
        // Parse defensively — non-JSON bodies are possible.
        const ctx = error as unknown as { context?: Response }
        let code   = 'INQUIRY_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON — keep the transport message */ }

        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }

      if (!data) {
        throw new AppError({
          code:        'EMPTY_RESPONSE',
          message:     'No data returned from request-quote',
          userMessage: 'We could not record your inquiry. Please try again in a moment.',
          status:      500,
        })
      }

      return data
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'INVALID_BODY':
      return 'Please double-check your email address and the form fields, then try again.'
    case 'RATE_LIMITED':
      return 'You have submitted several inquiries in a short window. Please wait a few minutes before trying again.'
    case 'METHOD_NOT_ALLOWED':
      return 'The form is misconfigured. Please contact support directly.'
    case 'AUDIT_FAILED':
      // The lead row was stored — the operator already has it. Tell
      // the visitor everything is fine; the audit gap is server-side.
      return 'Thanks — your inquiry was received. We will be in touch shortly.'
    case 'DB_ERROR':
    case 'EMPTY_RESPONSE':
    case 'INQUIRY_FAILED':
    default:
      return detail || 'We could not record your inquiry. Please try again in a moment.'
  }
}
